"use client";

import { ReactNode, useCallback, useRef } from "react";
import { cn } from "utils/utils";
import { useIsomorphicLayoutEffect } from "modules/common/hooks/useIsomorphicLayoutEffect";

interface DataViewPanelProps {
  children: ReactNode;
  className?: string;
}

/**
 * Above-the-fold panel for the data views (grids/tables) on list pages: it
 * runs from wherever it sits down to the bottom of the viewport, so the view
 * inside it owns the only scrollbar on the screen.
 *
 * The height comes from the panel's measured distance to the top of the screen
 * rather than a per-page constant. Every fixed guess — `100svh-200px`,
 * `75vh`, `100svh-260px` — is only correct for the one header height it was
 * measured against, and the header is not one height: at `md` the pages grow a
 * toolbar row, which pushed the panel ~90px past the fold and gave the screen
 * a page scrollbar *and* a panel scrollbar.
 *
 * Never add `grow` to this: flex-grow treats a height as a basis rather than a
 * cap, so the panel would absorb its container's free space and clip below the
 * fold again. Same reason its PageContainer must not carry `min-h-screen`.
 */
export function DataViewPanel({ children, className }: DataViewPanelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const publishedTop = useRef(-1);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    // The panel's RESTING offset — where its top sits with everything
    // scrolled to zero — not its live viewport position. A live position
    // shrinks as the page scrolls, which grows the panel, which lengthens
    // the page, which gives the user more to scroll: the panel visibly
    // elongates as you scroll a short list. Adding back the scroll of every
    // ancestor (the `html` element included, so window scroll is covered)
    // makes the value scroll-independent, so the height never moves.
    let top = Math.round(el.getBoundingClientRect().top);
    for (let p = el.parentElement; p; p = p.parentElement) top += p.scrollTop;
    top = Math.max(0, top);
    // Only write on a real change: the panel's own height is derived from
    // this value, so an unconditional write would feed the observer below
    // its own output every frame.
    if (top === publishedTop.current) return;
    publishedTop.current = top;
    el.style.setProperty("--panel-top", `${top}px`);
  }, []);

  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    measure();

    // The body box catches viewport reflow. Preceding siblings at every level
    // catch position-only shifts: a tab can add or remove a toolbar row while
    // leaving the body's own border box unchanged, which still moves this
    // panel and changes the height available below it.
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    for (
      let node: Element | null = el;
      node && node !== document.body;
      node = node.parentElement
    ) {
      for (
        let sibling = node.previousElementSibling;
        sibling;
        sibling = sibling.previousElementSibling
      ) {
        observer.observe(sibling);
      }
    }
    // No scroll listener: the measurement is scroll-independent by
    // construction, so scrolling cannot change it — and re-measuring on every
    // scroll frame would only buy a forced layout.

    return () => observer.disconnect();
  }, [measure]);

  return (
    <div ref={ref} className={cn("data-view-panel", className)}>
      {children}
    </div>
  );
}
